// Edition form for player card

import * as Yup from 'yup';
import GuidedTour from './GuidedTour';
import CardEditTemplate from './CardEditTemplate';
import isMobilePhone from 'validator/lib/isMobilePhone';
import isEmail from 'validator/lib/isEmail';
import Input from './Input';
import ContactsFields from './ContactsFields';
import { userUpdate, userCreate } from '../utils/firebase';
import { useFirebase } from './FirebaseContextProvider';
import {
  capFirstLetter,
  decapFirstLetter,
  omitConsent,
  wrapNormalizeDates,
  wrapProfileUpdate,
} from '../utils/misc';
import InputDate from './InputDate';
import Select from './Select';
import { Genders, UsersRoles } from '../utils/enums';
import placeholderPerson from '../assets/images/placeholder-person.png';
import './PlayerCardEdit.scss';

const steps = [
  {
    target: '.player-card-edit',
    title: 'Personal information',
    content: 'Fill in your personal information first to access the app',
  },
];

// user-create takes the card whole: all three fields or none. user-update
// accepts a partial one, so this guards the create path only.
const cardFieldRequired = Yup.mixed()
  .nullable()
  .test('card-all-or-none', 'Required', function (value) {
    const { number, issueDate, expireDate } = this.parent;
    return !(number || issueDate || expireDate) || !!value;
  });

export default function PlayerCardEdit({ player }) {
  const { userIsPlayer, user } = useFirebase();
  const {
    id,
    name,
    phone,
    email,
    birthDate,
    card,
    gender,
    verified,
    teams,
    emailConsent,
    smsConsent,
  } = player;

  const isOwnProfile = user?.id === id;
  const isNew = id === 'new';

  // Consent is the person's own to give: pre-tick it only on one's own blank
  // card, where the ticks are still clickable.
  const freshOwnCard = isOwnProfile && !name;

  const initialValues = {
    name: name || '',
    phone: phone || '',
    email: email || '',
    birthDate: birthDate ? birthDate.toDate() : null,
    gender: gender || '',
    card: {
      expireDate: card?.expireDate ? card.expireDate.toDate() : null,
      issueDate: card?.issueDate ? card.issueDate.toDate() : null,
      number: card?.number || '',
    },
    emailConsent: freshOwnCard || !!emailConsent,
    smsConsent: freshOwnCard || !!smsConsent,
  };

  const cardField = isNew ? cardFieldRequired : Yup.mixed().nullable();

  const validationSchema = new Yup.ObjectSchema({
    name: Yup.string().max(100, 'Too long').required('Required'),
    gender: Yup.string().required('Required'),
    phone: Yup.string()
      .required('Required')
      .test('is-valid-phone', 'Invalid', (value) =>
        value ? isMobilePhone(value, 'en-US', { strictMode: false }) : false,
      ),
    email: Yup.string()
      .test('is-valid-email', 'Invalid', (value) => isEmail(value))
      .required('Required'),
    birthDate: Yup.date().required('Required'),
    card: Yup.object({
      number: cardField,
      issueDate: cardField,
      expireDate: cardField,
    }),
  });

  return (
    <GuidedTour steps={userIsPlayer ? steps : []} tourKey='player-card-edit-v1'>
      <CardEditTemplate
        className='player-card-edit'
        showUploadWarning
        id={id}
        imgPath={player.imgPath}
        imgThumbPath={player.imgThumbPath}
        hasImage={!!player.imgPath}
        placeholder={placeholderPerson}
        formConfig={{
          initialValues,
          validationSchema,
        }}
        update={wrapProfileUpdate(
          wrapNormalizeDates((payload) => userUpdate({ id, ...payload })),
          isOwnProfile,
        )}
        create={wrapNormalizeDates((payload) =>
          // user-create rejects unknown keys: consent is the player's own to
          // give, and only user-update takes it, from that account itself.
          userCreate({
            role: UsersRoles.PLAYER,
            teams,
            ...omitConsent(payload),
          }),
        )}
        afterCreateNavPath={-1}
        requireImage
      >
        {(formik) => (
          <>
            <div className='general-info'>
              <Input
                name='name'
                placeholder='Name'
                className='name'
                {...formik.getFieldProps('name')}
                error={
                  formik.touched.name && formik.errors.name
                    ? formik.errors.name
                    : null
                }
                disabled={!!verified}
              />
              <Select
                options={Object.values(Genders).map((gen) =>
                  capFirstLetter(gen),
                )}
                value={capFirstLetter(formik.values.gender)}
                onChange={(val) =>
                  formik.setFieldValue('gender', decapFirstLetter(val))
                }
                placeholder='Gender'
                error={
                  formik.touched.gender && formik.errors.gender
                    ? formik.errors.gender
                    : null
                }
                disabled={!!verified}
              />
              <InputDate
                value={formik.values.birthDate}
                onChange={async (newDate) => {
                  await formik.setFieldValue('birthDate', newDate);
                }}
                className='birth-date'
                maxDate={new Date()}
                placeholder='Birth date'
                error={
                  formik.touched.birthDate && formik.errors.birthDate
                    ? formik.errors.birthDate
                    : null
                }
                disabled={!!verified}
              />
            </div>
            <ContactsFields
              formik={formik}
              emailDisabled={!isNew}
              consentDisabled={!isOwnProfile}
            />
            <div className='card-wrap'>
              <Input
                name='card.number'
                placeholder='Card number'
                {...formik.getFieldProps('card.number')}
                error={
                  formik.touched.card?.number && formik.errors.card?.number
                    ? formik.errors.card?.number
                    : null
                }
              />
              <InputDate
                value={formik.values.card.issueDate}
                onChange={async (newDate) => {
                  await formik.setFieldValue('card.issueDate', newDate);
                }}
                className='issue-date'
                placeholder='Card issue date'
                error={
                  formik.touched.card?.issueDate &&
                  formik.errors.card?.issueDate
                    ? formik.errors.card?.issueDate
                    : null
                }
              />
              <InputDate
                value={formik.values.card.expireDate}
                onChange={async (newDate) => {
                  await formik.setFieldValue('card.expireDate', newDate);
                }}
                className='expire-date'
                placeholder='Card exp date'
                error={
                  formik.touched.card?.expireDate &&
                  formik.errors.card?.expireDate
                    ? formik.errors.card?.expireDate
                    : null
                }
              />
            </div>
          </>
        )}
      </CardEditTemplate>
    </GuidedTour>
  );
}
